export const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export function defaultDayHours(day, { closedDay = "Mon", openFrom = "12:00", openTo = "21:30" } = {}) {
    const open = day !== closedDay;
    return {
        day,
        open,
        periods: open ? [{ from: openFrom, to: openTo }] : [],
    };
}

export function defaultOperatingHours(options = {}) {
    return WEEKDAYS.map((day) => defaultDayHours(day, options));
}

export function normalizeDayHours(row) {
    if (!row?.day) return null;

    if (Array.isArray(row.periods)) {
        const periods = row.periods
            .map((period) => ({
                from: String(period?.from || "").trim(),
                to: String(period?.to || "").trim(),
            }))
            .filter((period) => period.from || period.to);

        return {
            day: row.day,
            open: Boolean(row.open),
            periods: row.open ? (periods.length ? periods : [{ from: "", to: "" }]) : [],
        };
    }

    const from = String(row.from || "").trim();
    const to = String(row.to || "").trim();
    return {
        day: row.day,
        open: Boolean(row.open),
        periods: row.open
            ? (from || to ? [{ from, to }] : [{ from: "", to: "" }])
            : [],
    };
}

export function normalizeOperatingHours(hours) {
    if (!Array.isArray(hours) || !hours.length) {
        return defaultOperatingHours();
    }

    const byDay = Object.fromEntries(
        hours.map(normalizeDayHours).filter(Boolean).map((row) => [row.day, row])
    );

    return WEEKDAYS.map((day) => byDay[day] || defaultDayHours(day, { closedDay: day }));
}

export function formatDayPeriods(dayRow) {
    if (!dayRow?.open) return "Closed";
    const periods = (dayRow.periods || []).filter((period) => period.from && period.to);
    if (!periods.length) return "Closed";
    return periods.map((period) => `${period.from}–${period.to}`).join(", ");
}

function dayScheduleKey(row) {
    return formatDayPeriods(row);
}

export function formatDayRange(days) {
    if (!days?.length) return "";
    const indexes = days
        .map((day) => WEEKDAYS.indexOf(day))
        .filter((index) => index >= 0)
        .sort((a, b) => a - b);

    if (!indexes.length) return "";

    const ranges = [];
    let start = indexes[0];
    let end = indexes[0];

    for (let i = 1; i < indexes.length; i += 1) {
        if (indexes[i] === end + 1) {
            end = indexes[i];
            continue;
        }
        ranges.push(start === end ? WEEKDAYS[start] : `${WEEKDAYS[start]}–${WEEKDAYS[end]}`);
        start = indexes[i];
        end = indexes[i];
    }
    ranges.push(start === end ? WEEKDAYS[start] : `${WEEKDAYS[start]}–${WEEKDAYS[end]}`);
    return ranges.join(", ");
}

export function formatOperatingHoursSummary(hours, { maxDays = null, includeClosed = false } = {}) {
    const grouped = groupOperatingHours(hours, { includeClosed });
    const limited = Number.isFinite(maxDays) && maxDays > 0 ? grouped.slice(0, maxDays) : grouped;
    const summary = limited
        .map((row) => `${row.daysLabel} ${row.label}`)
        .join(" · ");

    return summary || "Hours coming soon";
}

export function operatingHoursRows(hours) {
    return normalizeOperatingHours(hours).map((row) => ({
        day: row.day,
        label: formatDayPeriods(row),
        open: Boolean(row.open) && (row.periods || []).some((period) => period.from && period.to),
    }));
}

/** Compact rows: same hours collapsed, e.g. Mon, Wed–Fri · 11:00–14:30, 17:30–23:30 */
export function groupOperatingHours(hours, { includeClosed = true } = {}) {
    const normalized = normalizeOperatingHours(hours);
    const buckets = new Map();

    for (const row of normalized) {
        const label = dayScheduleKey(row);
        const open = label !== "Closed";
        if (!includeClosed && !open) continue;
        if (!buckets.has(label)) {
            buckets.set(label, { days: [], label, open });
        }
        buckets.get(label).days.push(row.day);
    }

    return [...buckets.values()].map((group) => ({
        days: group.days,
        daysLabel: formatDayRange(group.days),
        label: group.label,
        open: group.open,
        key: `${group.days.join("-")}:${group.label}`,
    }));
}

export function serializeOperatingHours(hours) {
    return normalizeOperatingHours(hours).map((row) => ({
        day: row.day,
        open: row.open,
        periods: row.open
            ? row.periods.map((period) => ({ from: period.from, to: period.to }))
            : [],
    }));
}

export function updateDayHours(hours, day, updater) {
    return normalizeOperatingHours(hours).map((row) => (row.day === day ? updater(row) : row));
}

export function updateDayPeriod(hours, day, periodIndex, field, value) {
    return updateDayHours(hours, day, (row) => ({
        ...row,
        periods: row.periods.map((period, index) => (
            index === periodIndex ? { ...period, [field]: value } : period
        )),
    }));
}

export function addDayPeriod(hours, day) {
    return updateDayHours(hours, day, (row) => {
        const lastPeriod = row.periods[row.periods.length - 1];
        const nextPeriod = lastPeriod?.from && lastPeriod?.to
            ? { from: "16:00", to: "22:00" }
            : { from: "", to: "" };
        return {
            ...row,
            open: true,
            periods: [...row.periods, nextPeriod],
        };
    });
}

export function removeDayPeriod(hours, day, periodIndex) {
    return updateDayHours(hours, day, (row) => {
        const nextPeriods = row.periods.filter((_, index) => index !== periodIndex);
        return {
            ...row,
            periods: nextPeriods.length ? nextPeriods : [{ from: "", to: "" }],
        };
    });
}

export function setDayOpen(hours, day, open) {
    return updateDayHours(hours, day, (row) => ({
        ...row,
        open,
        periods: open ? (row.periods.length ? row.periods : [{ from: "", to: "" }]) : [],
    }));
}

function clonePeriods(periods = []) {
    return periods.map((period) => ({ from: period.from, to: period.to }));
}

export function applySourceDayToDays(hours, sourceDay, targetDays) {
    const source = normalizeOperatingHours(hours).find((row) => row.day === sourceDay);
    if (!source) return hours;

    const targets = new Set(targetDays.filter((day) => day !== sourceDay));
    return normalizeOperatingHours(hours).map((row) => {
        if (!targets.has(row.day)) return row;
        return {
            ...row,
            open: source.open,
            periods: source.open ? clonePeriods(source.periods) : [],
        };
    });
}

export const WEEKDAY_GROUPS = {
    all: WEEKDAYS,
    weekdays: ["Mon", "Tue", "Wed", "Thu", "Fri"],
    weekend: ["Sat", "Sun"],
};
